import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client, { formatZAR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CHECKOUT } from "@/constants/testIds";
import { toast } from "sonner";

export default function Checkout() {
    const { lines, subtotal } = useCart();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const paymentIssue = params.get("payment");
    const [form, setForm] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem("uk_checkout_form")) || { name: "", email: "", phone: "", notes: "" };
        } catch (_) {
            return { name: "", email: "", phone: "", notes: "" };
        }
    });
    const method = "payfast";
    const [submitting, setSubmitting] = useState(false);
    const [accepting, setAccepting] = useState(true);
    useEffect(() => {
        client.get("/settings").then((r) => setAccepting(r.data.accepting_orders !== false)).catch(() => {});
    }, []);

    if (lines.length === 0) {
        return (
            <div className="container-wide py-24 text-center">
                <p className="font-script text-brand-gold text-3xl">Empty basket</p>
                <h1 className="font-display text-3xl mt-2">Your cart is empty</h1>
                <button className="btn-primary mt-6" onClick={() => nav("/order")}>Browse menu</button>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                customer: { ...form, email: form.email.trim() || null },
                payment_method: method,
                lines: lines.map((l) => ({
                    item_id: l.item_id,
                    name: l.name,
                    price: l.price,
                    quantity: l.quantity,
                    modifiers: l.modifiers || [],
                    line_total: l.line_total,
                })),
            };
            const { data } = await client.post("/orders", payload);
            const orderId = data.order.id;

            if (data.payfast) {
                // Post to PayFast via dynamic form
                const f = document.createElement("form");
                f.method = "POST";
                f.action = data.payfast.url;
                Object.entries(data.payfast.fields).forEach(([k, v]) => {
                    const inp = document.createElement("input");
                    inp.type = "hidden";
                    inp.name = k;
                    inp.value = v ?? "";
                    f.appendChild(inp);
                });
                document.body.appendChild(f);
                sessionStorage.setItem("uk_pending_payfast", orderId);
                sessionStorage.setItem("uk_checkout_form", JSON.stringify(form));
                f.submit();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not place order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container-wide py-14 grid lg:grid-cols-[1fr_400px] gap-10">
            <form onSubmit={submit} className="space-y-6">
                <p className="font-script text-brand-gold text-3xl">Almost there</p>
                <h1 className="font-display text-4xl">Checkout</h1>
                {paymentIssue && (
                    <div data-testid="checkout-payment-issue" role="alert" className="border border-brand-gold/50 bg-brand-gold/10 px-4 py-3 rounded-sm text-sm">
                        <p className="text-brand-gold font-medium">
                            {paymentIssue === "failed" ? "Your payment didn't go through." : "Your payment was cancelled."}
                        </p>
                        <p className="text-brand-cream/80 mt-1">
                            No money was taken and your order wasn't placed. Your cart is still here, so you can try again.
                        </p>
                    </div>
                )}
                <p className="text-brand-mute text-sm max-w-md">
                    Pickup only. We'll SMS/WhatsApp you when your order is ready.
                </p>

                <div className="grid gap-4">
                    <input
                        data-testid={CHECKOUT.name}
                        required
                        placeholder="Full name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-brand-charcoal border border-brand-line px-4 py-3 rounded-sm focus:outline-none focus:border-brand-olive"
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <input
                            data-testid={CHECKOUT.email}
                            required
                            type="email"
                            placeholder="Email (for your order confirmation)"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="bg-brand-charcoal border border-brand-line px-4 py-3 rounded-sm focus:outline-none focus:border-brand-olive"
                        />
                        <input
                            data-testid={CHECKOUT.phone}
                            required
                            placeholder="Cellphone (0790000000)"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="bg-brand-charcoal border border-brand-line px-4 py-3 rounded-sm focus:outline-none focus:border-brand-olive"
                        />
                    </div>
                    <textarea
                        data-testid={CHECKOUT.notes}
                        rows={3}
                        placeholder="Notes for the kitchen (allergies, prep timing…)"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="bg-brand-charcoal border border-brand-line px-4 py-3 rounded-sm focus:outline-none focus:border-brand-olive resize-none"
                    />
                </div>

                <div className="space-y-3">
                    <p className="text-xs tracking-[0.2em] text-brand-gold uppercase">Payment</p>
                    <div
                        data-testid={CHECKOUT.payfast}
                        className="flex items-center gap-3 border px-4 py-3 rounded-sm border-brand-olive bg-brand-olive/10"
                    >
                        <div>
                            <p className="text-sm font-medium">Card via PayFast (secure)</p>
                            <p className="text-xs text-brand-mute">Credit / Debit / Instant EFT — you'll be redirected to PayFast to pay.</p>
                        </div>
                    </div>
                </div>

                {!accepting && (
                    <p data-testid="checkout-closed-notice" className="text-sm text-brand-gold border border-brand-gold/40 px-4 py-3 rounded-sm">
                        We're not taking online orders right now. Please call or WhatsApp us.
                    </p>
                )}
                <button
                    data-testid={CHECKOUT.submit}
                    disabled={submitting || !accepting}
                    className="btn-primary w-full"
                >
                    {submitting ? "Placing order…" : `Place Order · ${formatZAR(subtotal)}`}
                </button>
            </form>

            <aside className="lg:sticky lg:top-24 h-fit border border-brand-line p-6 rounded-sm bg-brand-charcoal">
                <p className="font-script text-brand-gold text-3xl">Your Order</p>
                <div className="mt-4 divide-y divide-brand-line">
                    {lines.map((l, i) => (
                        <div key={i} className="py-3">
                            <div className="flex justify-between">
                                <p className="text-sm">{l.quantity} × {l.name}</p>
                                <p className="text-sm text-brand-gold">{formatZAR(l.line_total)}</p>
                            </div>
                            {l.modifiers?.length > 0 && (
                                <p className="text-xs text-brand-mute mt-1">
                                    {l.modifiers.map((m) => m.name).join(", ")}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-4 pt-4 border-t border-brand-line flex justify-between">
                    <span>Total</span>
                    <span className="text-brand-gold text-lg">{formatZAR(subtotal)}</span>
                </div>
                <p className="text-[11px] text-brand-mute mt-2">VAT inclusive. Pickup only (Phase 1).</p>
            </aside>
        </div>
    );
}
